import { FC, Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Column, Menu } from '@robocorp/components';
import { IconSettingsSliders } from '@robocorp/icons/iconic';
import { FILTER_SETTINGS_SIZES } from '~/hooks/useFilterSettings';

type ModifiedColumn = Column & { menuTitle?: string };

export interface SettingsFilterMenuProps {
  columns: ModifiedColumn[];
  visibility: Record<string, boolean>;
  size: (typeof FILTER_SETTINGS_SIZES)[number];
  sizes?: (typeof FILTER_SETTINGS_SIZES)[number][];
  onSizeChange: (size: (typeof FILTER_SETTINGS_SIZES)[number]) => void;
  onVisibilityChange: (visibility: Record<string, boolean>) => void;
}

export const useSettingsFilter = ({
  columns,
  visibility,
  onVisibilityChange,
}: Pick<SettingsFilterMenuProps, 'columns' | 'visibility' | 'onVisibilityChange'>) => {
  const onChange = useCallback(
    (columnId: string) => () => {
      onVisibilityChange({ ...visibility, [columnId]: !visibility[columnId] });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibility],
  );

  const groups = useMemo(() => Array.from(new Set(columns.map((col) => col.groupName))), [columns]);
  return { groups, onChange };
};

export const FilterSettingsMenu: FC<SettingsFilterMenuProps> = ({
  columns,
  visibility,
  size,
  sizes = FILTER_SETTINGS_SIZES,
  onVisibilityChange,
  onSizeChange,
}) => {
  const { t } = useTranslation();

  const { groups, onChange } = useSettingsFilter({ columns, visibility, onVisibilityChange });

  return (
    <Menu
      size="small"
      trigger={<Button variant="ghost" aria-label="Filter settings" icon={IconSettingsSliders} />}
      minWidth={240}
      leaveMenuOpenOnItemSelect
    >
      {groups.length > 0 && <Menu.Title>{t('filter.setting.visibility')}</Menu.Title>}
      {groups.map((group) => (
        <Fragment key={group ?? 'unnamed'}>
          {group && <Menu.Title>{group}</Menu.Title>}
          {columns.map((column) => {
            const columnTitle = column.menuTitle?.toString() ?? column.title?.toString();

            if (column.required) {
              return null;
            }

            return (
              columnTitle &&
              columnTitle.length > 0 &&
              column.groupName === group && (
                <Menu.Checkbox
                  key={column.id}
                  onClick={onChange(column.id)}
                  checked={!column.hidden && !visibility[column.id]}
                  disabled={column.required}
                >
                  {columnTitle}
                </Menu.Checkbox>
              )
            );
          })}
        </Fragment>
      ))}
      {sizes.length > 0 && <Menu.Title>{t('filter.setting.rows')}</Menu.Title>}
      {sizes.map((rowSize) => (
        <Menu.Radio key={rowSize} checked={rowSize === size} onClick={() => onSizeChange(rowSize)}>
          {rowSize}
        </Menu.Radio>
      ))}
    </Menu>
  );
};
